import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Droplets, LogOut, FileDown, ExternalLink, Plus, CheckCircle2, Clock, Sparkles, Calendar, ShieldCheck, AlertTriangle, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { MembershipCard } from "@/components/MembershipCard";
import { HealthScoreGauge } from "@/components/passport/HealthScoreGauge";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import RequestQuoteButton from "@/components/RequestQuoteButton";

const STATUS_STYLE = {
  pending:    { label: "Awaiting payment", bg: "bg-amber-50", text: "text-amber-700", icon: Clock },
  paid:       { label: "Booked",           bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2 },
  received:   { label: "Received",         bg: "bg-blue-50", text: "text-blue-700", icon: Droplets },
  processing: { label: "In testing",       bg: "bg-cyan-50", text: "text-cyan-700", icon: Sparkles },
  completed:  { label: "Results ready",    bg: "bg-purple-50", text: "text-purple-700", icon: Sparkles },
  cancelled:  { label: "Cancelled",        bg: "bg-rose-50", text: "text-rose-700", icon: Clock },
};

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [propertyCount, setPropertyCount] = useState(0);
  const [passportItems, setPassportItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchParams.get("membership") === "success") {
      toast.success("Welcome to the AWARE Water Health Record — membership activated.");
      // Clear the query so a refresh doesn't re-toast
      const params = new URLSearchParams(searchParams);
      params.delete("membership"); params.delete("session_id"); params.delete("simulated");
      setSearchParams(params, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/customer/bookings").then(({ data }) => setBookings(data)),
      api.get("/passport").then(({ data }) => { setPropertyCount(data.length); setPassportItems(data); }).catch(() => {}),
      api.get("/passport/reminders").then(({ data }) => setReminders(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const totalSpend = bookings.filter((b) => b.status !== "cancelled").reduce((s, b) => s + (b.total || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <BrandLogo to="/" size="sm" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-slate-900">{user?.full_name}</span>
              <span className="text-[10px] uppercase tracking-wider text-cyan-600">Customer</span>
            </div>
            <Link
              to="/account/sign-in-methods"
              data-testid="customer-signin-methods-link"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              title="Sign-in methods"
            >
              <KeyRound className="w-4 h-4" />
            </Link>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={async () => { await logout(); window.location.assign("/"); }}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">My Water</div>
        <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">Hi {user?.full_name?.split(" ")[0]}</h1>

        {/* AWARE Water Health Record Membership */}
        <div className="mt-7">
          <MembershipCard />
        </div>

        {/* Passport entry banner */}
        <Link
          to="/passport"
          data-testid="customer-passport-cta"
          className="mt-7 group block rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-white p-6 lg:p-8 relative overflow-hidden hover:shadow-xl transition-shadow"
        >
          <div className="absolute -top-24 -right-16 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400">
                <ShieldCheck className="w-3.5 h-3.5" /> AWARE Water Health Record
              </div>
              <h2 className="font-display text-2xl lg:text-3xl font-bold mt-2">My properties &amp; trends</h2>
              <p className="text-sm text-slate-300 mt-1.5 max-w-xl">
                {propertyCount > 0
                  ? `Track water quality across ${propertyCount} ${propertyCount === 1 ? "property" : "properties"} — Health Score™, trend graphs, retest reminders.`
                  : "Your permanent water-quality record will populate after your first test."}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-sm font-semibold group-hover:bg-white group-hover:text-slate-900 transition-colors">
              View Water Record <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>

        {/* Jun 2026 — My Water: properties at a glance with latest
            score and one-tap rebook (prefills test + property). */}
        {passportItems.length > 0 && (
          <div data-testid="my-water-properties" className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {passportItems.map(({ property, latest_test }) => (
              <div
                key={property.id}
                data-testid={`my-water-property-${property.id}`}
                className="rounded-3xl bg-white border border-slate-200 p-5 flex flex-col"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{property.nickname || property.address}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{property.address}</div>
                  </div>
                  {latest_test && <HealthScoreGauge score={latest_test.health_score} band={latest_test.band} size={72} />}
                </div>
                <div className="mt-4 flex items-center gap-2 pt-3 border-t border-slate-100">
                  <Link
                    to={`/passport/property/${property.id}`}
                    data-testid={`my-water-view-${property.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold transition-colors"
                  >
                    View record
                  </Link>
                  <Link
                    to={`/book?test=${latest_test?.test_id || "tank"}&property=${property.id}`}
                    data-testid={`my-water-rebook-${property.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors"
                  >
                    Rebook <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Retest reminders */}
        {reminders.length > 0 && reminders.some((r) => r.window !== "scheduled") && (
          <div data-testid="customer-reminders" className="mt-5 rounded-3xl bg-amber-50 border border-amber-200 p-5 lg:p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-display font-semibold text-amber-900">Retest reminders</h3>
                <div className="mt-3 space-y-2">
                  {reminders.filter((r) => r.window !== "scheduled").slice(0, 3).map((r) => {
                    const overdue = r.days_until_due < 0;
                    return (
                      <div key={r.property_id + r.source_id} className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl p-3 border border-amber-100">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 text-sm truncate">{r.nickname || r.address}</div>
                          <div className="text-xs text-slate-500">
                            {overdue ? `Overdue by ${-r.days_until_due} days` : r.days_until_due === 0 ? "Due today" : `Due in ${r.days_until_due} days`}
                          </div>
                        </div>
                        <Link
                          to={`/book?test=${r.test_id}&property=${r.property_id}`}
                          data-testid={`customer-reminder-rebook-${r.property_id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors"
                        >
                          One-click rebook <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mt-7 grid sm:grid-cols-3 gap-4">
          <div className="rounded-3xl bg-white border border-slate-200 p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">My tests</div>
            <div className="font-display text-4xl font-bold text-slate-900 mt-2">{bookings.length}</div>
            <p className="text-xs text-slate-500 mt-1">Total bookings</p>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Completed</div>
            <div className="font-display text-4xl font-bold text-slate-900 mt-2">{bookings.filter((b) => b.status === "completed").length}</div>
            <p className="text-xs text-slate-500 mt-1">Reports delivered</p>
          </div>
          <div className="rounded-3xl bg-slate-900 text-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-400">Lifetime spend</div>
            <div className="font-display text-4xl font-bold mt-2">${totalSpend.toFixed(2)}</div>
            <p className="text-xs text-slate-300 mt-1">Online prepaid</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-10 flex items-center justify-between gap-4">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Your test history</h2>
          <Link to="/book" className="btn-aqua inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold">
            <Plus className="w-4 h-4" /> Book another test
          </Link>
        </div>

        {/* Bookings */}
        <div className="mt-5">
          {loading ? (
            <div className="text-slate-500 py-10 text-center">Loading your bookings...</div>
          ) : bookings.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center">
              <Droplets className="w-10 h-10 text-cyan-500 mx-auto mb-4" />
              <h3 className="font-display font-semibold text-slate-900 text-lg">No bookings yet</h3>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">When you book a water test using this email, it will automatically appear here.</p>
              <Link to="/book" className="btn-aqua mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold">
                Book your first test <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => {
                const style = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
                const Icon = style.icon;
                return (
                  <div
                    key={b.id}
                    data-testid={`customer-booking-${b.reference}`}
                    className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-mono text-sm font-bold text-slate-900">{b.reference}</div>
                        <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatDateAU(b.created_at)}
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
                        <Icon className="w-3 h-3" /> {style.label}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Test</div>
                        <div className="text-slate-900 font-medium">{b.test_name}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total paid</div>
                        <div className="text-slate-900 font-bold">${b.total.toFixed(2)}</div>
                      </div>
                      <div className="flex items-end justify-start lg:justify-end gap-2">
                        <Link
                          to={`/track/${b.reference}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-700 text-xs font-semibold transition-colors"
                          data-testid={`customer-track-${b.reference}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Track
                        </Link>
                        <a
                          href={`${apiBase}/api/bookings/${b.reference}/labels.pdf`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-700 text-xs font-semibold transition-colors"
                          data-testid={`customer-pdf-${b.reference}`}
                        >
                          <FileDown className="w-3.5 h-3.5" /> Labels
                        </a>
                      </div>
                    </div>
                    {b.status === "completed" && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-slate-500">
                          <b>Report ready.</b> If treatment was recommended, ask your local Pristine Water Systems specialist for a quote.
                        </div>
                        <RequestQuoteButton
                          bookingRef={b.reference}
                          authenticated={true}
                          variant="compact"
                          dataTestId={`customer-request-quote-${b.reference}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Help block */}
        <div className="mt-12 grid sm:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="font-display font-semibold text-slate-900">Need to ask about a result?</h3>
            <p className="text-sm text-slate-500 mt-1">Email us with your booking reference and we'll get back same business day.</p>
            <a href="mailto:lab@awarewatertesting.com.au" className="mt-3 inline-flex items-center gap-1 text-cyan-600 font-semibold text-sm hover:underline">lab@awarewatertesting.com.au &rarr;</a>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="font-display font-semibold text-slate-900">Want personalised advice?</h3>
            <p className="text-sm text-slate-500 mt-1">Your next test report includes a plain-English Summary with Recommended Actions. If treatment is suggested, you can request a free Pristine Water Systems estimate or assessment from the report.</p>
            <Link to="/book" className="mt-3 inline-flex items-center gap-1 text-cyan-600 font-semibold text-sm hover:underline">Book a test →</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
